import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Layout, { DateInput } from "./Layout";
import { getApiUrl, getTodayIso } from "./config";

function VehicleTrip() {
  const role = localStorage.getItem("role") || "user";
  const isAdmin = role === "admin";
  const [vehicles, setVehicles] = useState([]);
  const [products, setProducts] = useState([]);
  const singleVehicleMode = vehicles.length === 1;
  const singleVehicleId = singleVehicleMode ? String(vehicles[0]?.id || "") : "";
  const latestFetchRef = useRef(0);
  const [loadingStock, setLoadingStock] = useState(false);
  const [stockError, setStockError] = useState("");

  const [startTrip, setStartTrip] = useState({
    vehicle_id: "",
    start_km: "",
    start_date: getTodayIso(),
  });
  const [loadItems, setLoadItems] = useState([]);
  const [currentLoadItem, setCurrentLoadItem] = useState({
    product_id: "",
    quantity: 0,
    weight: 0,
  });

  const [unloadVehicleId, setUnloadVehicleId] = useState("");
  const [openTrip, setOpenTrip] = useState(null);
  const [vehicleStock, setVehicleStock] = useState([]);
  const [endTrip, setEndTrip] = useState({
    end_km: "",
    end_date: getTodayIso(),
    dead_qty: 0,
    weight_difference: 0,
  });

  const fetchMasters = async () => {
    const [vRes, pRes] = await Promise.all([
      axios.get(`${getApiUrl()}/vehicles`),
      axios.get(`${getApiUrl()}/products`),
    ]);
    setVehicles(vRes.data || []);
    setProducts(pRes.data || []);
  };

  const addLoadItem = () => {
    if (!currentLoadItem.product_id) return;
    const qty = parseInt(currentLoadItem.quantity) || 0;
    const wt = parseFloat(currentLoadItem.weight) || 0;
    if (qty <= 0 && wt <= 0) return;
    const prod = products.find((p) => p.id === parseInt(currentLoadItem.product_id));
    setLoadItems([
      ...loadItems,
      {
        product_id: parseInt(currentLoadItem.product_id),
        product_name: prod ? prod.name : "",
        quantity: qty,
        weight: wt,
      },
    ]);
    setCurrentLoadItem({ product_id: "", quantity: 0, weight: 0 });
  };

  const removeLoadItem = (idx) => {
    setLoadItems(loadItems.filter((_, i) => i !== idx));
  };

  const startVehicleTrip = async () => {
    if (!startTrip.vehicle_id) return alert("Select vehicle");
    if (String(startTrip.start_km).trim() === "") return alert("Enter start KM");
    if (loadItems.length === 0) return alert("Add at least one item");
    try {
      await axios.post(`${getApiUrl()}/vehicle-trips/start`, {
        ...startTrip,
        start_km: parseInt(startTrip.start_km),
        items: loadItems.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          weight: i.weight,
        })),
      });
      alert("Trip started");
      setStartTrip({
        vehicle_id: "",
        start_km: "",
        start_date: getTodayIso(),
      });
      setLoadItems([]);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.sqlMessage ||
        err?.response?.data?.code ||
        "Failed to start trip";
      alert(message);
    }
  };

  const fetchOpenTripAndStock = async (vehicleId) => {
    if (!vehicleId) {
      setOpenTrip(null);
      setVehicleStock([]);
      setStockError("");
      return;
    }
    const fetchId = ++latestFetchRef.current;
    setLoadingStock(true);
    setStockError("");
    try {
      const tripRes = await axios.get(`${getApiUrl()}/vehicle-trips/open`, {
        params: { vehicle_id: vehicleId },
      });
      if (latestFetchRef.current === fetchId) setOpenTrip(tripRes.data);
      const stockRes = await axios.get(`${getApiUrl()}/vehicle-stock/${vehicleId}`);
      if (latestFetchRef.current === fetchId) setVehicleStock(stockRes.data || []);
    } catch (err) {
      console.error(err);
      if (latestFetchRef.current === fetchId) {
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.sqlMessage ||
          err?.response?.data?.code ||
          "Failed to fetch vehicle stock";
        setStockError(message);
        setOpenTrip(null);
        setVehicleStock([]);
      }
    } finally {
      if (latestFetchRef.current === fetchId) setLoadingStock(false);
    }
  };

  const endVehicleTrip = async () => {
    if (!openTrip) return alert("No open trip for this vehicle");
    if (String(endTrip.end_km).trim() === "") return alert("Enter end KM");
    try {
      await axios.post(`${getApiUrl()}/vehicle-trips/${openTrip.id}/end`, {
        end_km: parseInt(endTrip.end_km),
        end_date: endTrip.end_date,
        dead_qty: parseInt(endTrip.dead_qty) || 0,
        weight_difference: parseFloat(endTrip.weight_difference) || 0,
      });
      alert("Trip closed and stock moved to godown");
      setEndTrip({ end_km: "", end_date: getTodayIso(), dead_qty: 0, weight_difference: 0 });
      fetchOpenTripAndStock(unloadVehicleId);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.sqlMessage ||
        err?.response?.data?.code ||
        "Failed to close trip";
      alert(message);
    }
  };

  const reopenTrip = async (tripId) => {
    if (!tripId) return;
    if (!window.confirm("Reopen this trip?")) return;
    try {
      await axios.post(`${getApiUrl()}/vehicle-trips/${tripId}/reopen`);
      alert("Trip reopened");
      fetchOpenTripAndStock(unloadVehicleId);
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.sqlMessage ||
        err?.response?.data?.code ||
        "Failed to reopen trip";
      alert(message);
    }
  };

  useEffect(() => {
    fetchMasters().catch(console.error);
  }, []);

  useEffect(() => {
    if (!singleVehicleMode || !singleVehicleId) return;
    setUnloadVehicleId((prev) => (prev ? prev : singleVehicleId));
    setStartTrip((prev) => ({ ...prev, vehicle_id: prev.vehicle_id ? prev.vehicle_id : singleVehicleId }));
  }, [singleVehicleMode, singleVehicleId]);

  useEffect(() => {
    if (!unloadVehicleId) return;
    fetchOpenTripAndStock(unloadVehicleId).catch(console.error);
    const t = setTimeout(() => {
      fetchOpenTripAndStock(unloadVehicleId).catch(console.error);
    }, 250);
    return () => clearTimeout(t);
  }, [unloadVehicleId]);

  return (
    <Layout>
      <div className="container mt-4">
        <h2>Vehicle Trip</h2>

        {!singleVehicleMode && (
          <div className="card p-4 mb-4 shadow-sm border-0">
            <h5>Start Trip</h5>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label fw-bold">Vehicle</label>
                <select
                  className="form-select"
                  value={startTrip.vehicle_id}
                  onChange={(e) => setStartTrip({ ...startTrip, vehicle_id: e.target.value })}
                >
                  <option value="">Select Vehicle</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.vehicle_no}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">Start KM</label>
                <input
                  type="number"
                  className="form-control"
                  value={startTrip.start_km}
                  onChange={(e) => setStartTrip({ ...startTrip, start_km: e.target.value })}
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">Date</label>
                <DateInput value={startTrip.start_date} onChange={(v) => setStartTrip({ ...startTrip, start_date: v })} />
              </div>
            </div>

            <div className="card p-3 mt-3 bg-light border-0">
              <h6>Add Items to Load</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Product</label>
                  <select
                    className="form-select"
                    value={currentLoadItem.product_id}
                    onChange={(e) => setCurrentLoadItem({ ...currentLoadItem, product_id: e.target.value })}
                  >
                    <option value="">Select Product</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Qty</label>
                  <input
                    type="number"
                    className="form-control"
                    value={currentLoadItem.quantity}
                    onChange={(e) => setCurrentLoadItem({ ...currentLoadItem, quantity: parseInt(e.target.value) })}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Weight</label>
                  <input
                    type="number"
                    className="form-control"
                    value={currentLoadItem.weight}
                    onChange={(e) => setCurrentLoadItem({ ...currentLoadItem, weight: parseFloat(e.target.value) })}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div className="col-md-2 d-grid align-items-end">
                  <button type="button" className="btn btn-primary" onClick={addLoadItem}>
                    Add
                  </button>
                </div>
              </div>

              {loadItems.length > 0 && (
                <div className="table-responsive mt-3">
                  <table className="table table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th>Product</th>
                        <th>Qty</th>
                        <th>Weight</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadItems.map((it, idx) => (
                        <tr key={idx}>
                          <td>{it.product_name}</td>
                          <td>{it.quantity}</td>
                          <td>{parseFloat(it.weight || 0).toFixed(2)}</td>
                          <td>
                            <button className="btn btn-danger btn-sm" onClick={() => removeLoadItem(idx)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="text-end mt-3">
              <button type="button" className="btn btn-success" onClick={startVehicleTrip}>
                Start Trip
              </button>
            </div>
          </div>
        )}

        <div className="card p-4 mb-5 shadow-sm border-0">
          <h5>{singleVehicleMode ? "Stock (Single Vehicle)" : "End Trip"}</h5>
          {!singleVehicleMode && (
            <>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-bold">Vehicle</label>
                  <select className="form-select" value={unloadVehicleId} onChange={(e) => setUnloadVehicleId(e.target.value)}>
                    <option value="">Select Vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.vehicle_no}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">End KM</label>
                  <input
                    type="number"
                    className="form-control"
                    value={endTrip.end_km}
                    onChange={(e) => setEndTrip({ ...endTrip, end_km: e.target.value })}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Date</label>
                  <DateInput value={endTrip.end_date} onChange={(v) => setEndTrip({ ...endTrip, end_date: v })} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Dead Qty</label>
                  <input
                    type="number"
                    className="form-control"
                    value={endTrip.dead_qty}
                    onChange={(e) => setEndTrip({ ...endTrip, dead_qty: parseInt(e.target.value) })}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">Weight Difference</label>
                  <input
                    type="number"
                    className="form-control"
                    value={endTrip.weight_difference}
                    onChange={(e) => setEndTrip({ ...endTrip, weight_difference: parseFloat(e.target.value) })}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              </div>

              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="fw-bold">Open Trip:</div>
                  <div>{openTrip ? `#${openTrip.id} (Start KM: ${openTrip.start_km})` : "-"}</div>
                </div>
              </div>
            </>
          )}

          {!singleVehicleMode && isAdmin && !openTrip && unloadVehicleId && (
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-warning"
                onClick={() => {
                  const tripId = window.prompt("Enter closed trip ID to reopen");
                  const idNum = Number(tripId);
                  if (!idNum) return alert("Enter a valid trip ID");
                  reopenTrip(idNum);
                }}
              >
                Reopen Closed Trip
              </button>
            </div>
          )}

          {!singleVehicleMode && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div className="fw-bold">Vehicle Stock</div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => fetchOpenTripAndStock(unloadVehicleId)}
                disabled={!unloadVehicleId || loadingStock}
              >
                {loadingStock ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          )}

          {!!stockError && (
            <div className="alert alert-warning mt-3 mb-0" role="alert">
              {stockError}
            </div>
          )}

          <div className="table-responsive mt-3">
            <table className="table table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Weight</th>
                </tr>
              </thead>
              <tbody>
                {vehicleStock.map((r) => (
                  <tr key={`${r.vehicle_id}-${r.product_id}`}>
                    <td>{r.product_name}</td>
                    <td>{r.quantity}</td>
                    <td>{parseFloat(r.weight || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {vehicleStock.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-center">
                      No vehicle stock
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!singleVehicleMode && (
            <div className="text-end">
              <button type="button" className="btn btn-danger" onClick={endVehicleTrip} disabled={!openTrip}>
                End Trip (Unload to Godown)
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default VehicleTrip;
